import { BasePdfGenerator } from "../../../../../utils/pdf/BasePdfGenerator";
import {
  buildAutoNote,
  buildAutoTable,
} from "../../../../../utils/pdf/buildAutoTable";
import { boldAutoColumn } from "../../../../../utils/pdf/page";
import { buildFormCSDraftPdfSections } from "./formCSDraftPdfData";

const footerSettings = false;
const EMPTY_CELL = "-";
const SECTION_TITLE_SPACING = 16;

const PDF_COLORS = {
  border: [212, 218, 226],
  headerBg: [232, 238, 247],
  headerText: [31, 44, 64],
  labelBg: [248, 250, 252],
  subsectionBg: [239, 246, 255],
  mutedText: [105, 113, 124],
  successText: [25, 118, 73],
  warningText: [154, 103, 0],
};

const buildFormCSScheduleMeta = (filingInfo, isSubmitted) => ({
  name: `${isSubmitted ? "Form C-S Submitted" : "Form C-S Draft Review"} (YA ${
    filingInfo?.ya ?? ""
  })`,
  type: "Form C-S",
  headerLabel: "Form C-S",
});

const buildFormCSHeaderCompany = (
  company,
  filingInfo,
  dataFormCS,
  referenceSummary
) => ({
  ...company,
  currency: referenceSummary?.currency ?? company?.currency,
  currencyRate: referenceSummary?.currencyRate ?? company?.currencyRate,
  manual_currency_rate:
    referenceSummary?.currencyRate ?? company?.manual_currency_rate,
  uen: filingInfo?.uen ?? "",
  yearOfAssessment: filingInfo?.ya ?? "",
  financialYearStart: dataFormCS?.basisPeriodFrom ?? "",
  financialYearEnd: dataFormCS?.basisPeriodTo ?? "",
});

const normalizePdfCellValue = (value) =>
  value == null || value === "" || value === "Select an option"
    ? EMPTY_CELL
    : value;

const isSubsectionRow = ({ label, value }) =>
  value === "" && label && typeof label === "object" && label.content;

const buildPdfSectionBody = (rows) =>
  rows.map((row) => {
    if (isSubsectionRow(row)) {
      return [
        {
          ...row.label,
          colSpan: 2,
          styles: {
            fillColor: PDF_COLORS.subsectionBg,
            textColor: PDF_COLORS.headerText,
            fontStyle: "bold",
            cellPadding: { top: 4, right: 3, bottom: 3, left: 3 },
          },
        },
      ];
    }

    return [row.label, normalizePdfCellValue(row.value)];
  });

const appendKeyValueSection = (
  pdf,
  company,
  schedule,
  title,
  rows,
  sectionNo
) => {
  if (!rows?.length) return;

  const body = buildPdfSectionBody(rows);
  const sectionLabel = `${String(sectionNo).padStart(2, "0")}  ${title}`;

  buildAutoTable({
    pdf,
    head: [["Section & Fields", "Amount & Details"]],
    body,
    schedule,
    company,
    addPlainTitle: true,
    tableTitle: sectionLabel,
    customHeadStyles: {
      fontStyle: "bold",
      halign: "left",
      fillColor: PDF_COLORS.headerBg,
      textColor: PDF_COLORS.headerText,
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
    },
    bodyStyles: {
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      valign: "top",
    },
    alternateRowStyles: {
      fillColor: [250, 251, 253],
    },
    columnStyles: {
      0: {
        halign: "left",
        cellWidth: 102,
        fillColor: PDF_COLORS.labelBg,
        textColor: PDF_COLORS.headerText,
        fontStyle: "bold",
      },
      1: { halign: "left", cellWidth: 76, textColor: [20, 26, 36] },
    },
    didParseCell: (data) => {
      if (data.section !== "body") return;

      const rawText = Array.isArray(data.cell.raw?.content)
        ? data.cell.raw.content.join(" ")
        : (data.cell.raw?.content ?? data.cell.raw);
      const text = String(rawText ?? "").trim();

      if (data.column.index === 1) {
        if (text === EMPTY_CELL) {
          data.cell.styles.textColor = PDF_COLORS.mutedText;
          data.cell.styles.fontStyle = "italic";
        } else if (text === "Yes" || text === "Applicable") {
          data.cell.styles.textColor = PDF_COLORS.successText;
          data.cell.styles.fontStyle = "bold";
        } else if (text === "No" || text === "Not Applicable") {
          data.cell.styles.textColor = PDF_COLORS.warningText;
        }
      }
    },
    printFooter: footerSettings,
    customStartY: pdf.doc.lastAutoTable.finalY + SECTION_TITLE_SPACING,
  });
};

export const generateFormCSDraftReviewPdf = (
  forView,
  company,
  formSnapshot,
  context = {},
  isExcel = false
) => {
  const pdf = new BasePdfGenerator(false, isExcel);
  const { filingInfo, dataFormCS, declaration, declarationAck } =
    formSnapshot || {};
  const isSubmitted = context.documentStatus === "submitted";
  const acknowledgementNo = context.acknowledgementNo || "";
  const estTaxPayable =
    context.estTaxPayable ?? context.estimatedTaxPayable ?? "";
  const schedule = buildFormCSScheduleMeta(filingInfo, isSubmitted);
  const headerCompany = buildFormCSHeaderCompany(
    company,
    filingInfo,
    dataFormCS,
    context.referenceSummary
  );

  const sections = buildFormCSDraftPdfSections({
    filingInfo,
    dataFormCS,
    declaration,
    declarationAck,
    selectedTc: context.selectedTc,
    referenceSummary: context.referenceSummary,
    isDevPdfExport: context.isDevPdfExport === true,
  });

  const summaryBody = [
    [
      boldAutoColumn("Document type"),
      isSubmitted
        ? "Form C-S - submitted data record"
        : "Form C-S — draft for review",
    ],
    ...(isSubmitted
      ? [
          [boldAutoColumn("Acknowledgement No."), acknowledgementNo || "—"],
          [boldAutoColumn("Estimated Tax Payable"), estTaxPayable || "—"],
        ]
      : []),
    [
      boldAutoColumn("UEN / YA"),
      `${filingInfo?.uen || "—"} / YA ${filingInfo?.ya ?? "—"}`,
    ],
    ...(Array.isArray(context.summaryRows)
      ? context.summaryRows
          .filter((row) => Array.isArray(row) && row[0])
          .map(([label, value]) => [boldAutoColumn(label), value || "—"])
      : []),
    [
      boldAutoColumn("Generated"),
      new Date().toLocaleString("en-SG", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    ],
    [boldAutoColumn("Review sections"), `${sections.length} sections`],
  ];

  buildAutoTable({
    pdf,
    head: [["Review item", "Details"]],
    body: summaryBody,
    schedule,
    company: headerCompany,
    addPlainTitle: true,
    tableTitle: isSubmitted
      ? "Form C-S submitted data"
      : "Form C-S submission draft",
    customHeadStyles: {
      fontStyle: "bold",
      halign: "left",
      fillColor: PDF_COLORS.headerBg,
      textColor: PDF_COLORS.headerText,
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
    },
    bodyStyles: {
      lineColor: PDF_COLORS.border,
      lineWidth: 0.1,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
    },
    alternateRowStyles: {
      fillColor: [250, 251, 253],
    },
    columnStyles: {
      0: {
        halign: "left",
        cellWidth: 72,
        fillColor: PDF_COLORS.labelBg,
      },
      1: { halign: "left", cellWidth: 106 },
    },
    printFooter: footerSettings,
  });

  sections.forEach(({ title, rows }, index) => {
    appendKeyValueSection(pdf, headerCompany, schedule, title, rows, index + 1);
  });

  const documentNote = isSubmitted
    ? "This application-generated document records the Form C-S data submitted to IRAS. " +
      "Refer to the IRAS acknowledgement for the official submission status."
    : "This document is not for any official submission and is not submitted to IRAS. " +
      "Verify all figures against your tax computation and supporting schedules before submission.";

  buildAutoNote(
    pdf,
    forView,
    headerCompany,
    schedule,
    documentNote,
    "",
    [],
    footerSettings,
    isSubmitted
      ? "Submitted Form C-S Data Record"
      : "Internal Draft Tax Statement (Not for Submission)"
  );

  return pdf.getDoc(forView, headerCompany, schedule);
};
